import type {DbFeature} from "../interfaces/Feature";

/** dbFeature properties that hold other dbFeatures (see dbFeatureRelatedFeaturesProperties) */
const RELATED_PROPERTIES = ["relatedFeatures", "bundledFeatures"] as const;

/**
 * Canonical dbFeature registry of a layout.
 *
 * Before: `layout.allDbFeatures` ({groupName: dbFeature[]}) was the only index, so every "is it loaded?" check
 * scanned arrays - O(N) per feature, O(N²) while loading a big group page by page.
 * Now `byId` answers it in O(1). `byGroup` IS the old `allDbFeatures` object (same instance), so all the
 * legacy `layout.allDbFeatures[groupName]` readers keep working unchanged.
 */
export class FeatureStore {
  readonly byId = new Map<number, DbFeature>();
  readonly byGroup: Record<string, DbFeature[]> = {};

  static getGroupKey(groupName?: string): string {
    return (groupName || "").toLowerCase();
  }

  get(id: number): DbFeature | undefined {
    return this.byId.get(id);
  }

  has(id: number): boolean {
    return this.byId.has(id);
  }

  get size(): number {
    return this.byId.size;
  }

  /**
   * Registers a NEW canonical dbFeature (caller checks `has()` first and merges duplicates itself).
   * The group key keeps the legacy behaviour: `groupName?.toLowerCase()` (undefined groupName -> "undefined").
   */
  add(dbFeature: DbFeature): void {
    const groupKey = dbFeature.groupName?.toLowerCase() as string;
    if (!this.byGroup[groupKey]) {
      this.byGroup[groupKey] = [];
    }
    this.byGroup[groupKey].push(dbFeature);
    this.byId.set(dbFeature.id, dbFeature);
  }

  group(groupName?: string): DbFeature[] {
    return this.byGroup[FeatureStore.getGroupKey(groupName)] || [];
  }

  /**
   * relatedFeatures / bundledFeatures arrays come straight from the response, so they can hold COPIES of
   * features we already have (un-slimmed, with stale prices). Points them to the canonical objects, in place.
   * @param dbFeature {DbFeature}
   */
  canonicalizeRelated(dbFeature: DbFeature): void {
    if (!dbFeature) {
      return;
    }
    for (let p = 0; p < RELATED_PROPERTIES.length; p++) {
      const list = dbFeature[RELATED_PROPERTIES[p]];
      if (!Array.isArray(list)) {
        continue;
      }
      for (let i = 0; i < list.length; i++) {
        const item = list[i];
        if (!item) {
          continue;
        }
        const canonical = this.byId.get(item.id);
        if (canonical && canonical !== item) {
          list[i] = canonical;
        }
      }
    }
  }
}

/**
 * Builds a store from an existing legacy `allDbFeatures` object (e.g. a layout without a store)
 * @param allDbFeatures {Record<string, DbFeature[]>}
 */
export const getDbFeaturesByIdMap = (allDbFeatures: Record<string, DbFeature[]> = {}): Map<number, DbFeature> => {
  const byId = new Map<number, DbFeature>();
  Object.keys(allDbFeatures).forEach(groupName => {
    const dbFeatures = allDbFeatures[groupName];
    if (!Array.isArray(dbFeatures)) {
      return;
    }
    for (let i = 0; i < dbFeatures.length; i++) {
      byId.set(dbFeatures[i].id, dbFeatures[i]);
    }
  });
  return byId;
};

/**
 * O(1) lookup on a layout when it has a store, falls back to scanning `allDbFeatures` (fake/mocked layouts)
 * @param layout {object}
 * @param id {number}
 */
export const getLoadedDbFeature = (
  layout: {featureStore?: FeatureStore; allDbFeatures?: Record<string, DbFeature[]>} | null | undefined,
  id: number
): DbFeature | undefined => {
  if (!layout) {
    return undefined;
  }
  if (layout.featureStore) {
    return layout.featureStore.get(id);
  }
  const allDbFeatures = layout.allDbFeatures || {};
  for (const groupName of Object.keys(allDbFeatures)) {
    const list = allDbFeatures[groupName] || [];
    for (let i = 0; i < list.length; i++) {
      if (list[i].id === id) {
        return list[i];
      }
    }
  }
  return undefined;
};
